import { siteConfig } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        body.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pi03li2vhsdh2d3500', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || responseText
          || '提交失败，请稍后重试';
        if (msg.includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 4688.tv 官方在线视频平台"
        description={`${siteConfig.description} 了解4688.tv品牌故事、发展历程和联系方式，专业在线视频平台值得信赖。`}
        keywords="4688.tv 关于我们,4688.tv 品牌介绍,4688.tv 联系方式,在线视频平台"
        canonicalUrl="/about"
      />

      <SchemaOrg
        type="Organization"
        data={{
          name: siteConfig.name,
          url: siteConfig.url,
          description: siteConfig.description,
          foundingDate: String(siteConfig.foundingYear),
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: siteConfig.contact.phone,
            email: siteConfig.contact.email,
            contactType: 'customer service',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: siteConfig.contact.address,
            addressCountry: 'CN',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://4688.tv' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://4688.tv/about' },
          ],
        }}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="py-10 md:py-16">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于 4688.tv
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {siteConfig.description}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {[
                { value: siteConfig.stats.totalContent, label: '海量内容' },
                { value: siteConfig.stats.activeUsers, label: '活跃用户' },
                { value: siteConfig.stats.dailyViews, label: '日播放量' },
                { value: `${siteConfig.stats.coverage} 国家/地区`, label: '全球覆盖' },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-xl p-5 md:p-6 text-center"
                >
                  <div className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px" />
                <div className="space-y-8">
                  {siteConfig.milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className={`relative flex gap-6 md:gap-0 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50" />
                      <div className={`pl-10 md:pl-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full mb-2 whitespace-nowrap">
                          {m.year}
                        </span>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                      </div>
                      <div className="hidden md:block md:w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {siteConfig.features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-xl p-5 md:p-6 hover:bg-background-200/70 transition-colors"
                  >
                    <div className="w-11 h-11 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl mb-4">
                      <i className={`${feature.icon} text-xl`} />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <section id="contact" className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何问题或合作意向，欢迎随时与我们取得联系
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.email}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">客服电话</p>
                  <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.phone}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                  <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.address}</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                  发送消息
                </h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 text-accent-600 rounded-full mb-4">
                      <i className="ri-check-line text-2xl" />
                    </div>
                    <p className="text-foreground-900 font-medium">消息已发送成功！</p>
                    <p className="text-sm text-foreground-500 mt-1">我们会尽快与您取得联系</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    {formStatus === 'error' && (
                      <div className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                        {formError}
                      </div>
                    )}
                    <input
                      type="text"
                      name="company_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（不超过500字）"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </section>
          </section>
        </div>
      </main>
    </>
  );
}